import React, { useCallback, useState } from 'react';

import { saveAs } from 'file-saver';
import { Button, Modal, Table } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useUpdateEffect } from 'react-use';

import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { fileMaxSize10mb } from 'constants/constants';

interface IShippingConfirmationModalProps {
  onClose: () => void;
}

interface IShippingResult {
  imei: string;
  status: number;
}
const TABLE_ROWS_PREVIEW_SIZE = 5;

const ShippingConfirmationModal = ({ onClose }: IShippingConfirmationModalProps) => {
  const { formatMessage } = useIntl();
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [results, setResults] = useState<IShippingResult[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);

  const [{ data, loading, error }, post] = useAxios<IShippingResult[]>(
    {
      method: 'POST',
      url: '/clinician-gateway/device/fulfillment'
    },
    { manual: true }
  );

  const parseCsv = (csvText: string): any[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length === 0) return [];

    const headers = lines[0].split(',').map((header) => header.trim().replace(/"/g, ''));
    const rows = lines.slice(1);

    return rows.map((row) => {
      const values = row.split(',').map((value) => value.trim().replace(/"/g, ''));
      const rowObject: any = {};
      headers.forEach((header, index) => {
        rowObject[header] = values[index] || '';
      });
      return rowObject;
    });
  };

  const mapRowToDeviceDto = (row: any) => ({
    orderId: row['Purchase Order ID'] ?? '',
    sku: row['Product Code'] ?? '',
    imei: row['Serial Number'] ?? '',
    trackingNumber: row['Tracking Number'] ?? '',
    postalCode: row['Postal Code'] ?? ''
  });

  const onDropAccepted = useCallback(
    async (acceptedFiles: any) => {
      if (acceptedFiles[0]) {
        const file = acceptedFiles[0];
        const reader = new FileReader();

        reader.onload = async (e) => {
          const csvText = e.target?.result as string;
          const parsedData = parseCsv(csvText);
          setCsvData(parsedData);
          const devices = parsedData.map(mapRowToDeviceDto);
          await post({ data: { devices } });
        };

        reader.readAsText(file);
      }
    },
    [post]
  );

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    setUploadError(true);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxFiles: 1,
    multiple: false,
    accept: ['.csv'],
    maxSize: fileMaxSize10mb / 10
  });

  const downloadResultsAsCsv = () => {
    if (results.length === 0) return;

    const headers = [formatMessage({ id: 'imeiKey' }), formatMessage({ id: 'statusKey' })];
    const csvContent = [
      headers.join(','),
      ...results.map((result) => `"${result.imei}","${result.status}"`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `shipping_confirmation_results_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const getStatusBadgeOptions = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'danger';
    return 'secondary';
  };

  useUpdateEffect(() => {
    if (data) {
      setResults(data);
      setShowResults(true);
    }
  }, [data]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal show onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{formatMessage({ id: 'shippingConfirmationKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {uploadError && <Error errorMessage={formatMessage({ id: 'fileUploadErrorKey' })} />}
        {loading ? (
          <Loading />
        ) : showResults ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>{formatMessage({ id: 'resultsKey' })}</h6>
              <Button variant="outline-primary" size="sm" onClick={downloadResultsAsCsv}>
                {formatMessage({ id: 'downloadResultsKey' })}
              </Button>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>{formatMessage({ id: 'imeiKey' })}</th>
                    <th>{formatMessage({ id: 'statusKey' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr key={index}>
                      <td>{result.imei}</td>
                      <td>
                        <span className={`badge bg-${getStatusBadgeOptions(result.status)}`}>
                          {result.status >= 200 && result.status < 300
                            ? formatMessage({ id: 'successKey' })
                            : `${formatMessage({ id: 'errorKey' }).toUpperCase()} ${result.status}`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <>
            {error && <Error errorMessage={formatMessage({ id: 'fulfillmentErrorKey' })} />}
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <p className="drag-n-drop">
                {formatMessage(
                  {
                    id: 'dragAndDropKey'
                  },
                  { size: '1Mb', types: 'CSV' }
                )}
              </p>
            </div>
            {csvData.length > 0 && (
              <div className="mt-3">
                <h6>{formatMessage({ id: 'previewKey' })}</h6>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        {Object.keys(csvData[0] || {}).map((header, index) => (
                          <th key={index}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.slice(0, TABLE_ROWS_PREVIEW_SIZE).map((row, index) => (
                        <tr key={index}>
                          {Object.values(row).map((value: any, cellIndex) => (
                            <td key={cellIndex}>{value}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {csvData.length > TABLE_ROWS_PREVIEW_SIZE && (
                    <small className="text-muted">
                      {formatMessage(
                        { id: 'showingFirstRowsKey' },
                        { count: TABLE_ROWS_PREVIEW_SIZE, total: csvData.length }
                      )}
                    </small>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ShippingConfirmationModal;
